import { useCallback, useEffect, useState } from 'react';
import { LogIn } from 'lucide-react';
import { api, ErroApi } from '../../api/cliente';
import type { AcessoLog } from '../../api/tipos';
import {
  Abas,
  Aviso,
  Cartao,
  Celula,
  Esqueleto,
  Etiqueta,
  Tabela,
  Td,
  Th,
  Vazio,
} from '../../componentes/ui';
import { Pagina, TituloPagina } from '../../componentes/Pagina';
import { dataHora, numero } from '../../util/formato';
import css from './AcessosAdmin.module.css';

const FILTROS = [
  { valor: 'todos', rotulo: 'Todos' },
  { valor: 'falhas', rotulo: 'Só as falhas' },
] as const;

type Filtro = (typeof FILTROS)[number]['valor'];

/** Motivos gravados pela API, traduzidos para quem lê a tela. */
const MOTIVOS: Record<string, string> = {
  'senha-invalida': 'Senha incorreta',
  'email-desconhecido': 'E-mail não cadastrado',
  'conta-desativada': 'Conta bloqueada',
};

/** Recarrega sozinha: a tela existe para flagrar tentativa em andamento. */
const INTERVALO_MS = 20_000;

/**
 * Histórico de acessos.
 *
 * As falhas valem mais que os sucessos: uma sequência de "e-mail não
 * cadastrado" partindo do mesmo IP é varredura de credenciais, e uma sequência
 * de "senha incorreta" contra UMA conta é tentativa dirigida. O filtro existe
 * para separar esse sinal do volume normal de logins.
 */
export function AcessosAdmin() {
  const [filtro, setFiltro] = useState<Filtro>('todos');
  const [itens, setItens] = useState<AcessoLog[] | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    const q = new URLSearchParams({ limite: '100' });
    if (filtro === 'falhas') q.set('apenasFalhas', 'true');
    try {
      setErro(null);
      setItens(await api<AcessoLog[]>(`/admin/acessos?${q.toString()}`));
    } catch (err) {
      setErro(
        err instanceof ErroApi ? err.message : 'Falha ao carregar os acessos.',
      );
    }
  }, [filtro]);

  useEffect(() => {
    void carregar();
    const t = setInterval(() => void carregar(), INTERVALO_MS);
    return () => clearInterval(t);
  }, [carregar]);

  const falhas = itens?.filter((a) => !a.sucesso).length ?? 0;

  return (
    <Pagina largura="larga">
      <TituloPagina
        titulo="Acessos"
        descricao="Últimas 100 tentativas de login, atualizadas a cada 20 segundos."
        acoes={
          <Abas rotulo="Filtrar tentativas" opcoes={FILTROS} atual={filtro} aoMudar={setFiltro} />
        }
      />

      {erro && <Aviso>{erro}</Aviso>}

      {itens && falhas >= 10 && filtro === 'todos' && (
        <Aviso tom="alerta">
          {numero(falhas)} das últimas {numero(itens.length)} tentativas falharam. Vale olhar
          se partem do mesmo IP.
        </Aviso>
      )}

      <Cartao padding={0}>
        {!itens ? (
          <div className={css.carregando}>
            <Esqueleto altura={20} />
            <Esqueleto altura={20} />
            <Esqueleto altura={20} />
          </div>
        ) : itens.length === 0 ? (
          <Vazio
            icone={<LogIn />}
            titulo="Nenhum acesso registrado"
            descricao="Nada foi registrado ainda com este filtro."
          />
        ) : (
          <Tabela rotulo="Tentativas de login">
            <thead>
              <tr>
                <Th>Quando</Th>
                <Th>Conta</Th>
                <Th>Resultado</Th>
                <Th>IP</Th>
                <Th>Dispositivo</Th>
              </tr>
            </thead>
            <tbody>
              {itens.map((a) => (
                <tr key={a.id}>
                  <Td className={css.quando}>{dataHora(a.createdAt)}</Td>
                  <Td>
                    {a.user ? (
                      <Celula principal={a.user.nome} secundaria={a.email} />
                    ) : (
                      <>
                        <span className={css.inexistente}>Conta inexistente</span>
                        <span className={css.email}>{a.email}</span>
                      </>
                    )}
                  </Td>
                  <Td>
                    {a.sucesso ? (
                      <Etiqueta tom="ok">Entrou</Etiqueta>
                    ) : (
                      <div className={css.resultado}>
                        <Etiqueta tom="erro">Recusado</Etiqueta>
                        <span className={css.motivo}>
                          {MOTIVOS[a.motivo ?? ''] ?? a.motivo ?? '—'}
                        </span>
                      </div>
                    )}
                  </Td>
                  <Td className={css.ip}>{a.ip ?? '—'}</Td>
                  <Td>
                    <span className={css.dispositivo} title={a.userAgent ?? undefined}>
                      {a.userAgent ?? '—'}
                    </span>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Tabela>
        )}
      </Cartao>
    </Pagina>
  );
}
